import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import {
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import type { Recipe } from '@/types';

const CACHE_TTL_MS = 5 * 60 * 1000;
const QUERY_LIMIT = 200;

interface DiscoverState {
  publicRecipes: Recipe[];
  lastFetchedAt: number | null;
  isLoading: boolean;
  error: string | null;
  loadPublicRecipes: (opts?: { force?: boolean }) => Promise<void>;
  invalidate: () => void;
}

function isValidRecipe(d: unknown): d is Recipe {
  if (!d || typeof d !== 'object') return false;
  const r = d as Recipe;
  return (
    Boolean(r.id) &&
    Boolean(r.name) &&
    Array.isArray(r.ingredients) &&
    Array.isArray(r.steps)
  );
}

export const useDiscoverStore = create<DiscoverState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      publicRecipes: [],
      lastFetchedAt: null,
      isLoading: false,
      error: null,

      loadPublicRecipes: async (opts) => {
        const { lastFetchedAt, isLoading } = get();
        if (isLoading) return;

        const fresh =
          lastFetchedAt !== null && Date.now() - lastFetchedAt < CACHE_TTL_MS;
        if (fresh && !opts?.force) return;

        set({ isLoading: true });

        try {
          const snap = await getDocs(
            query(
              collectionGroup(db, 'recipes'),
              where('isPublic', '==', true),
              orderBy('createdAt', 'desc'),
              limit(QUERY_LIMIT)
            )
          );

          const currentUid = useAuthStore.getState().user?.uid;
          const publicRecipes = snap.docs
            .map((d) => d.data())
            .filter(isValidRecipe)
            .filter((r) => r.createdBy !== currentUid);

          set({
            publicRecipes,
            lastFetchedAt: Date.now(),
            isLoading: false,
            error: null,
          });
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : 'Failed to load public recipes',
            isLoading: false,
          });
        }
      },

      invalidate: () => {
        set({ lastFetchedAt: null });
      },
    })),
    { name: 'discover-store' }
  )
);
